import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import TableHeader from '../../../components/Common/TableHeader';
import { OrderDetailsModel } from '../../../interfaces/order.interface';
import { formatOrderCurrency } from '../../../utils/currencyFormat';

interface OrderHistoryTableProps {
  orderHistory: OrderDetailsModel[] | null;
  convertTimestampToDateTime: (timestamp: number) => string;
  calculateBookPurchaseTotalAmount: (order: OrderDetailsModel) => number;
  handleEyeClick: (order: OrderDetailsModel) => void;
  resendConfirmationEmail: (orderId: string) => void;
  resendEmailLoader: string | null;
}

/**
 * Buyer-facing shipping status. Canonical fulfillmentStatus first — the legacy `status` string
 * says "awaiting shipment" on pooled orders that are really parked for the teacher's Class
 * Shipment, which is exactly the wrong signal. Legacy statuses only for pre-migration orders.
 */
const shippingStatusLabel = (order: OrderDetailsModel): string => {
  const canonical: Record<string, string> = {
    awaiting_authorization: 'Reserved for Class Shipment',
    needs_address: 'Processing',
    preparing_assets: 'Preparing to ship',
    ready_to_dispatch: 'Preparing to ship',
    dispatched: 'Printing',
    in_production: 'Printing',
    shipped: 'Shipped',
    error: 'Processing',
    canceled: 'Canceled',
    not_applicable: '-',
  };
  const legacy: Record<string, string> = {
    pending_shipment: 'Reserved for Class Shipment',
    queueing_shipment: 'Preparing to ship',
    awaiting_shipment: 'Preparing to ship',
  };
  const fulfillmentStatus = (order as any)?.fulfillmentStatus as string | undefined;
  if (fulfillmentStatus && canonical[fulfillmentStatus]) return canonical[fulfillmentStatus];
  const status = order?.status as string | undefined;
  if (status && legacy[status]) return legacy[status];
  return (status && status.replace(/_/g, ' ')) || '-';
};

const OrderHistoryTable: React.FC<OrderHistoryTableProps> = ({
                                                               orderHistory,
                                                               convertTimestampToDateTime,
                                                               calculateBookPurchaseTotalAmount,
                                                               handleEyeClick,
                                                               resendConfirmationEmail,
                                                               resendEmailLoader
                                                             }) => {
  return (
    <table className="min-w-full border border-gray-300">
      {TableHeader([
        'Date',
        'Description',
        'Qty',
        'Book Number',
        'Sub-Total',
        'Discount',
        'Total',
        'Free Physical Books',
        'Shipping Status',
        ' '
      ])}
      <tbody>
      {orderHistory?.map((order, index) => (
        <tr key={index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
          <td className="py-2 text-left px-4 border-b">
            {convertTimestampToDateTime(order?.timestamp)}
          </td>
          <td className="py-2 text-left px-4 border-b capitalize">
            {order?.orderType && order?.orderType.replace('_', ' ')}
          </td>
          <td className="py-2 text-left px-4 border-b">{order?.quantity}</td>
          <td className="py-2 text-left px-4 border-b">{order?.orderNumber || '-'}</td>
          <td className="py-2 text-left px-4 border-b">
            {formatOrderCurrency(
              (parseFloat(order?.price) + parseFloat(order?.additionalPageCost?.toString() || '0')),
              order?.currency,
              order?.currencySymbol
            )}
          </td>
          <td className="py-2 text-left px-4 border-b">
            {(parseFloat(order?.price) + parseFloat(order?.additionalPageCost?.toString() || '0')) <= 0
              ? formatOrderCurrency(0, order?.currency, order?.currencySymbol)
              : formatOrderCurrency(order?.couponDiscount || 0, order?.currency, order?.currencySymbol)}
          </td>
          <td className="py-2 text-left px-4 border-b">
            {formatOrderCurrency(
              calculateBookPurchaseTotalAmount(order),
              order?.currency,
              order?.currencySymbol
            )}
          </td>
          <td className="py-2 text-left px-4 border-b capitalize">
            {(order?.freePhysicalBookCreated && order?.freePhysicalBookCreated) || '-'}
          </td>
          <td className="py-2 text-left px-4 border-b">
            {shippingStatusLabel(order)}
          </td>
          <td className="py-2 text-left px-4">
            <div className="flex justify-center items-center text-center gap-2">
              <FontAwesomeIcon
                icon={faEye}
                className="cursor-pointer"
                title="View Confirmation"
                onClick={() => handleEyeClick(order)}
              />
              {resendEmailLoader !== order?.id ? (
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className="cursor-pointer"
                  title="Send Confirmation Email"
                  onClick={() => resendConfirmationEmail(order?.id)}
                />
              ) : (
                <img className="w-8 h-8" src="/loading.gif" alt="Loading" />
              )}
            </div>
          </td>
        </tr>
      ))}
      </tbody>
    </table>
  );
};

export default OrderHistoryTable;
